import { StateSetter, useState } from 'react';

import { actions, loaders } from 'entities';

import {
  Box,
  Button,
  Column,
  LabeledInput,
  LabeledSelect,
  ResponsiveOverlay,
  Row,
} from 'components/Blocks';
import { FileUpIcon, SendIcon } from 'components/Icons';
import { TruncateText } from 'components/TruncateText';

import { useLang } from 'hooks';
import { useTaskModal } from '../../context.taskModal';

import { omit } from 'utils/object';
import { isImage } from 'utils/download';
import { fileNameToReadable } from 'utils/text';
import { getID } from 'utils/id';
import { sortByName } from 'utils/array';

interface PreUploadFileConfig {
  comment: string;

  files: Array<{
    file: File;
    url: string;
    category?: 'NEW_VERSION' | 'NEW_FILE';

    fileId?: string | null;

    name?: string | null;
    nameOption?: string | null;
  }>;
}

type FileConfig = PreUploadFileConfig['files'][number];

export function useEnsureCommentFileState() {
  const [commentKey, setCommentKey] = useState(getID());

  const [preUploadFiles, setPreUploadFiles] = useState<PreUploadFileConfig | null>(null);

  const initFileValidation = (comment: string, files: File[]) => {
    setPreUploadFiles({
      comment,
      files: files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    });
  };

  const finishFileValidation = () => {
    // Ensures we reset the comment input/file to nothing
    if (preUploadFiles) setCommentKey(getID());

    setPreUploadFiles(null);
  };

  return {
    commentKey,
    preUploadFiles,
    setPreUploadFiles,
    initFileValidation,
    finishFileValidation,
  };
}

type NormalizeCommentFilesModalProps = {
  preUploadFiles: PreUploadFileConfig | null;
  setPreUploadFiles: StateSetter<PreUploadFileConfig | null>;

  createComment: ReturnType<(typeof actions)['projects']['createTaskComment']['use']>;
};

const FREE_TYPING_ID = '!!_______free________!!';

export function NormalizeCommentFilesModal({
  preUploadFiles,
  setPreUploadFiles,
  createComment,
}: NormalizeCommentFilesModalProps) {
  const { t } = useLang();

  const { task } = useTaskModal();

  const [service, serviceState] = loaders.teams.findService.useV2({
    params: { id: task.service },
    blockLoad: !preUploadFiles,
  });

  const updateFile = (targetIndex: number, updater: (file: FileConfig) => FileConfig) => {
    setPreUploadFiles((old) => ({
      ...old!,
      files: old!.files.map((file, index) => (targetIndex === index ? updater(file) : file)),
    }));
  };

  const setCategory = (category: NonNullable<FileConfig['category']>, index: number) => {
    updateFile(index, (file) => ({
      ...file,
      category,
      fileId: category === 'NEW_VERSION' ? file.fileId : null,
      name: category === 'NEW_FILE' ? file.name : null,
      nameOption: category === 'NEW_FILE' ? file.nameOption : null,
    }));
  };

  const setVersionId = (versionId: string, index: number) => {
    updateFile(index, (file) => ({
      ...omit(file, ['name', 'nameOption']),
      fileId: versionId,
    }));
  };

  const setNameOption = (nameOption: string, index: number) => {
    updateFile(index, (file) => ({
      ...omit(file, ['fileId']),
      nameOption,
      name: nameOption === FREE_TYPING_ID ? '' : nameOption,
    }));
  };

  const setName = (name: string, index: number) => {
    updateFile(index, (file) => ({
      ...omit(file, ['fileId']),
      name: file.nameOption === FREE_TYPING_ID ? name : file.nameOption,
    }));
  };

  const filenameSuggestions = [
    ...(service?.fileNames || []).sort().map((label) => ({ label, value: label })),
    { value: FREE_TYPING_ID, label: t('other') },
  ];

  const okFilling = preUploadFiles?.files.every(
    (file) => file.category && (file.category === 'NEW_VERSION' ? file.fileId : file.name?.trim()),
  );

  const newFileVersion = actions.projects.newTaskFileVersion.use();

  const executeCreation = async () => {
    const newVersions = preUploadFiles!.files.filter((f) => f.category === 'NEW_VERSION');

    const commentFiles = preUploadFiles!.files.filter((f) => f.category === 'NEW_FILE');

    await Promise.all([
      ...newVersions.map(async (config) =>
        newFileVersion.execute({
          file: config.file,
          fileId: config.fileId!,
          projectId: task.projectId,
          taskId: task.id,
        }),
      ),

      createComment.execute({
        project: task.projectId,
        task: task.id,
        text: preUploadFiles!.comment,
        files: commentFiles.map((f) => ({
          file: f.file,
          name:
            f.nameOption === FREE_TYPING_ID
              ? f.name || fileNameToReadable(f.file.name)
              : f.nameOption!,
        })),
      }),
    ]);
  };

  return (
    <ResponsiveOverlay
      title={t('organize-files')}
      isOpen={!!preUploadFiles}
      close={() => setPreUploadFiles(null)}
      width="min(90vw, 560px)"
      className="max-w-full overflow-auto"
      showCloseButton={false}
      gap="6"
    >
      <p>{t('categorize-files')}</p>

      {preUploadFiles?.files.map((file, index) => (
        <Column key={file.file.name} className="rounded-md border p-3">
          {isImage(file.file.type) ? (
            <img src={file.url} className="size-12 rounded-md border" alt="" />
          ) : (
            <Box size={32} className="rounded-lg bg-primary/20 text-primary">
              <FileUpIcon />
            </Box>
          )}

          <TruncateText text={file.file.name} max={45} className="text-sm" />

          <LabeledSelect
            label={t('category')}
            fullWidth
            value={file.category || ''}
            onChangeDirect={(category) => setCategory(category as 'NEW_FILE', index)}
            options={[
              { value: 'NEW_FILE', label: t('brand-new-file') },
              { value: 'NEW_VERSION', label: t('new-version') },
            ]}
          />

          {file.category === 'NEW_FILE' && (
            <>
              <LabeledSelect
                label={t('file-name')}
                loading={serviceState.loading}
                options={filenameSuggestions}
                value={file.nameOption || ''}
                onChangeDirect={(opt) => setNameOption(opt, index)}
              />

              {file.nameOption === FREE_TYPING_ID && (
                <LabeledInput
                  label={t('file-name')}
                  value={file.name || ''}
                  onChangeDirect={(opt) => setName(opt, index)}
                />
              )}
            </>
          )}

          {file.category === 'NEW_VERSION' && (
            <LabeledSelect
              label={t('file-to-increase-version')}
              value={file.fileId || ''}
              onChangeDirect={(id) => setVersionId(id, index)}
              options={sortByName(task.files.filter((t) => t.name !== 'image')).map((taskFile) => ({
                value: taskFile.id,
                label: taskFile.name,
              }))}
            />
          )}
        </Column>
      ))}

      <Row justifyContent="space-between">
        <Button
          variant="ghost"
          className="-ml-2 w-fit"
          onClick={() => setPreUploadFiles(null)}
          loading={createComment.loading || newFileVersion.loading}
        >
          {t('cancel')}
        </Button>

        <Button
          className="w-fit"
          disabled={!okFilling}
          onClick={executeCreation}
          loading={createComment.loading || newFileVersion.loading}
        >
          <SendIcon />
          {t('send')}
        </Button>
      </Row>
    </ResponsiveOverlay>
  );
}
